import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import {
  Button, Message, Icon, Segment, Header
} from 'semantic-ui-react';
import {
  FormikInput,
  FormikTextArea,
  FormikSelect,
} from '../../pages/subPages/voiceActor/FormComponents';
import { literaryGroupTypes } from '../../constants/config';
import {
  fetchGenres,
  selectGenreOptions,
  selectGenreOptionsLoading,
} from '../../slices/genreOptionsSlice';
import {
  selectActivePrototype,
  selectPrototypeLoading,
  createPrototype,
  updatePrototype,
  uploadAndSetPrototypeImage,
} from '../../slices/litGroupPrototypeSlice';
import FileDropZone from './FileDropZone';

// Yup validation schema
const prototypeSchema = Yup.object().shape({
  name: Yup.string().trim().required('LitGroup name is required'),
  genre: Yup.string(),
  litGroupType: Yup.string(),
  authorName: Yup.string(),
  description: Yup.string(),
});

/**
 * PrototypeDetailsForm — Step 1: Define LitGroup prototype details
 * 
 * Uses Formik + custom FormComponents for consistent left-aligned styling.
 * Handles both CREATE (no activePrototype) and EDIT (activePrototype loaded).
 * 
 * Props:
 *   mode        — 'create' | 'edit'
 *   onCreated   — callback after successful creation
 *   onCancel    — callback to cancel creation mode
 */
const PrototypeDetailsForm = ({ mode = 'edit', onCreated, onCancel }) => {
  // The group's picture, chosen while the group does not yet exist.
  //
  // It cannot be uploaded here — the storage key is
  // aai-users/{userId}/litgroup-prototypes/{prototypeUuid}/groupImage/{fileName}, and there is
  // no prototypeUuid until the create call returns. So the file is held and uploaded the
  // instant it does: seconds, inside one submit, not across a wizard session.
  const [groupImageFile, setGroupImageFile] = useState(null);
  // A filename is a poor confirmation for a picture — it proves a file was chosen, not that it
  // was the right one, and there is no stored image to fall back on yet. So show the local file
  // itself. The object URL is revoked whenever the choice changes or the form goes away; leaking
  // them holds the whole image in memory for the life of the tab.
  const [groupImagePreview, setGroupImagePreview] = useState(null);
  const dispatch = useDispatch();
  const activePrototype = useSelector(selectActivePrototype);
  const loading = useSelector(selectPrototypeLoading);
  const genreOptions = useSelector(selectGenreOptions);
  const genreLoading = useSelector(selectGenreOptionsLoading);
  const userId = useSelector((state) => state.user?.profile?.data?.userId || null);
  const [saveMessage, setSaveMessage] = useState(null);

  // Build type dropdown options from literaryGroupTypes config
  const typeOptions = literaryGroupTypes.map(t => ({
    key: t.value,
    text: t.label,
    value: t.value,
  }));

  // Build genre dropdown options
  const genreDropdownOptions = (genreOptions || []).map(g => ({
    key: g.value || g,
    text: g.label || g.text || g,
    value: g.value || g,
  }));

  useEffect(() => {
    if (!groupImageFile) {
      setGroupImagePreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(groupImageFile);
    setGroupImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [groupImageFile]);

  // Fetch genres on mount
  useEffect(() => {
    if (!genreOptions || genreOptions.length === 0) {
      dispatch(fetchGenres());
    }
  }, [dispatch, genreOptions]);

  // Compute initial values based on mode
  const getInitialValues = () => {
    if (mode === 'edit' && activePrototype) {
      return {
        name: activePrototype.name || '',
        genre: activePrototype.genre || '',
        litGroupType: activePrototype.litGroupType || 'series',
        authorName: activePrototype.authorName || '',
        description: activePrototype.description || '',
      };
    }
    return {
      name: '',
      genre: '',
      litGroupType: 'series',
      authorName: '',
      description: '',
    };
  };

  const handleFormikSubmit = async (values, { setSubmitting }) => {
    setSaveMessage(null);
    try {
      if (mode === 'create') {
        const result = await dispatch(createPrototype(values)).unwrap();
        // The uuid exists now, so the staged picture has somewhere to go. Non-fatal: a group
        // without its picture is a cosmetic gap, and failing the creation over one would
        // discard everything the operator just typed.
        if (groupImageFile && result?.uuid) {
          try {
            await dispatch(uploadAndSetPrototypeImage({
              prototypeUuid: result.uuid, imageFile: groupImageFile, userId,
            })).unwrap();
          } catch (imgErr) {
            console.error('[PrototypeDetailsForm] group image upload failed:', imgErr);
          }
        }
        setSaveMessage({ type: 'success', text: `Literary Group "${result.name}" created!` });
        if (onCreated) onCreated(result);
      } else {
        await dispatch(updatePrototype({
          uuid: activePrototype.uuid,
          data: values,
        })).unwrap();
        setSaveMessage({ type: 'success', text: 'Literary Group updated.' });
      }
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setSaveMessage({ type: 'error', text: typeof err === 'string' ? err : 'Save failed.' });
    } finally {
      setSubmitting(false);
    }
  };

  const isLocked = activePrototype && ['processing', 'complete', 'archived'].includes(activePrototype.status);
  const isLoading = loading.create || loading.update;

  return (
    <Segment>
      <Header as="h3">
        <Icon name="settings" />
        <Header.Content>
          {mode === 'create' ? 'Create New Literary Group' : 'LitGroup Details'}
          <Header.Subheader>
            {mode === 'create'
              ? 'Define the details for your new LitGroup'
              : 'Edit the core metadata for this LitGroup'}
          </Header.Subheader>
        </Header.Content>
      </Header>

      <Formik
        initialValues={getInitialValues()}
        validationSchema={prototypeSchema}
        onSubmit={handleFormikSubmit}
        enableReinitialize
      >
        {({ handleSubmit, isValid, dirty, values }) => (
          <div>
            <Field
              name="name"
              component={FormikInput}
              label="LitGroup Name *"
              placeholder="Enter a name for this literary group..."
              disabled={isLocked}
              fluid
            />

            <div style={{ display: 'flex', gap: '1em', marginBottom: '1.0rem' }}>
              <div style={{ flex: 1 }}>
                <Field
                  name="genre"
                  component={FormikSelect}
                  label="Genre"
                  placeholder="Select genre..."
                  options={genreDropdownOptions}
                  loading={genreLoading}
                  disabled={isLocked}
                  search
                />
              </div>
              <div style={{ flex: 1 }}>
                <Field
                  name="litGroupType"
                  component={FormikSelect}
                  label="Type"
                  placeholder="Select type..."
                  options={typeOptions}
                  disabled={isLocked}
                  search
                />
              </div>
            </div>

            <Field
              name="authorName"
              component={FormikInput}
              label="Author Name"
              placeholder="Author full name..."
              disabled={isLocked}
              fluid
            />

            <Field
              name="description"
              component={FormikTextArea}
              label="Description"
              placeholder="Brief description of this literary group..."
              rows={3}
              disabled={isLocked}
            />

            {/* The group's own picture, offered at the moment the group is being defined.
                Only on CREATE — in edit mode the wizard's Details step already carries this
                block, and two of them would be one too many.

                It cannot upload here: the key needs a prototypeUuid that does not exist yet.
                So the file is held and sent the instant creation returns one — seconds,
                inside a single submit, not across a wizard session. */}
            {mode === 'create' && (
              <Segment style={{ marginTop: '1em' }}>
                <Header as="h5" style={{ marginBottom: '0.4em' }}>
                  <Icon name="images" />
                  <Header.Content>
                    Group Profile Image
                    <Header.Subheader>
                      Optional — the group&apos;s own picture, distinct from the author photo
                    </Header.Subheader>
                  </Header.Content>
                </Header>
                <FileDropZone
                  accept=".jpg,.jpeg,.png,.webp"
                  multiple={false}
                  label={groupImageFile ? groupImageFile.name : 'Drop a group image here'}
                  sublabel={groupImageFile
                    ? 'Click to choose a different one — uploaded as soon as the group is created'
                    : 'Uploaded as soon as the group is created'}
                  icon="images"
                  maxSizeMB={10}
                  disabled={isLocked}
                  previewSrc={groupImagePreview}
                  onFilesSelected={(files) => {
                    const f = Array.isArray(files) ? files[0] : files;
                    if (f) setGroupImageFile(f);
                  }}
                />
                {groupImageFile && (
                  <div style={{ textAlign: 'right', marginTop: '0.4em' }}>
                    <Button
                      size="mini"
                      basic
                      type="button"
                      disabled={isLocked}
                      onClick={() => setGroupImageFile(null)}
                    >
                      <Icon name="close" />Remove image
                    </Button>
                  </div>
                )}
              </Segment>
            )}

            {saveMessage && (
              <Message
                positive={saveMessage.type === 'success'}
                negative={saveMessage.type === 'error'}
                size="small"
              >
                <Icon name={saveMessage.type === 'success' ? 'check' : 'warning sign'} />
                {saveMessage.text}
              </Message>
            )}

            <div style={{ display: 'flex', gap: '0.5em', justifyContent: 'flex-end', marginTop: '10px' }}>
              {mode === 'create' && onCancel && (
                <Button onClick={onCancel} disabled={isLoading}>
                  Cancel
                </Button>
              )}
              {!isLocked && (
                <Button
                  primary
                  onClick={handleSubmit}
                  disabled={isLoading || !values.name.trim() || (mode !== 'create' && !dirty)}
                  loading={isLoading}
                >
                  <Icon name={mode === 'create' ? 'plus' : 'save'} />
                  {mode === 'create' ? 'Create Literary Group' : 'Save Changes'}
                </Button>
              )}
            </div>
          </div>
        )}
      </Formik>
    </Segment>
  );
};

export default PrototypeDetailsForm;
